import React from 'react';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Facebook, 
  Twitter, 
  Linkedin,
  Bot,
  ArrowUp
} from 'lucide-react';

const Footer = () => {
  const quickLinks = [
    { name: 'AI Automation', href: '#services' },
    { name: 'SEO Services', href: '#services' },
    { name: 'About Us', href: '#about' },
    { name: 'Success Stories', href: '#results' },
    { name: 'Contact', href: '#contact' }
  ];

  const services = [
    'AI Chatbots',
    'Email Automation',
    'Local SEO',
    'Video Production',
    'Social Media Management',
    'Reputation Management'
  ];

  const socialLinks = [
    {
      icon: Facebook,
      url: 'https://www.facebook.com/localfinderbiz/',
      name: 'Facebook'
    },
    {
      icon: Twitter,
      url: 'https://twitter.com/LocalfinderBiz/',
      name: 'Twitter'
    },
    {
      icon: Linkedin,
      url: 'https://www.linkedin.com/company/local-finder-llc/',
      name: 'LinkedIn'
    }
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-neutral-900 text-white">
      
      {/* Pre-footer CTA */}
      <div className="border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <div className="inline-flex items-center space-x-2 bg-primary-600/20 backdrop-blur-sm border border-primary-600/30 rounded-full px-4 py-2 mb-6">
              <Bot className="text-primary-400" size={16} />
              <span className="text-primary-300 text-sm font-medium">Ready to Get Started?</span>
            </div>
            
            <h3 className="text-3xl lg:text-4xl font-bold mb-4">
              Transform Your Business with AI Today
            </h3>
            <p className="text-xl text-neutral-400 mb-8 max-w-2xl mx-auto">
              Join hundreds of successful businesses that have revolutionized their operations 
              with Local Finder LLC's AI automation and proven marketing strategies.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="#contact"
                className="btn-primary flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>Get Your Free AI Audit</span>
                <Bot size={20} />
              </motion.a>
              <motion.a
                href="tel:+12133754006"
                className="btn-secondary flex items-center justify-center space-x-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Phone size={20} />
                <span>Call (213) 375-4006</span>
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-accent-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">LF</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">Local Finder LLC</h3>
                <p className="text-sm text-primary-400">GetBetterBiggerFasterVisibility</p>
              </div>
            </div>
            
            <p className="text-neutral-400 mb-6 max-w-md">
              Transforming local businesses by empowering them with expert marketing 
              and cutting-edge AI automation. Based in Pasadena, CA, serving businesses nationwide.
            </p>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <MapPin className="text-primary-400 flex-shrink-0" size={18} />
                <span className="text-neutral-300">12 S Raymond Ave, Pasadena, CA 91105</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="text-primary-400 flex-shrink-0" size={18} />
                <a href="tel:+12133754006"
                className="text-neutral-300 hover:text-white transition-colors duration-300">
                  (213) 375-4006
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="text-primary-400 flex-shrink-0" size={18} />
                <a href="mailto:contact@localfinder.biz" className="text-neutral-300 hover:text-white transition-colors duration-300">
                  contact@localfinder.biz
                </a>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            <h4 className="text-lg font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-neutral-400 hover:text-white transition-colors duration-300 flex items-center space-x-2"
                  >
                    <div className="w-1 h-1 bg-primary-400 rounded-full"></div>
                    <span>{link.name}</span>
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            <h4 className="text-lg font-semibold mb-6">Our Services</h4>
            <ul className="space-y-3">
              {services.map((service) => (
                <li key={service}>
                  <span className="text-neutral-400 flex items-center space-x-2">
                    <div className="w-1 h-1 bg-accent-400 rounded-full"></div>
                    <span>{service}</span>
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Social Media & Copyright */}
        <div className="border-t border-neutral-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            
            {/* Social Links */}
            <div className="flex items-center space-x-6">
              <span className="text-neutral-400">Follow us:</span>
              <div className="flex space-x-4">
                {socialLinks.map((social) => (
                  <motion.a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-neutral-800 hover:bg-primary-600 rounded-lg flex items-center justify-center text-neutral-400 hover:text-white transition-all duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <social.icon size={18} />
                  </motion.a>
                ))}
              </div>
            </div>

            {/* Copyright & Scroll to Top */}
            <div className="flex items-center space-x-6">
              <p className="text-neutral-500 text-sm">
                © {new Date().getFullYear()} Local Finder LLC. All rights reserved.
              </p>
              <motion.button
                onClick={scrollToTop}
                className="w-10 h-10 bg-neutral-800 hover:bg-primary-600 rounded-lg flex items-center justify-center text-neutral-400 hover:text-white transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <ArrowUp size={18} />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;